import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Link2 } from "lucide-react";

import { AlertNote } from "@/components/AlertNote";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { GoogleAnalyticsIcon } from "@/components/icons/GoogleAnalyticsIcon";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { useAuth } from "@/components/contexts/AuthContext";
import { getClientAnalyticsLink, getClientSearchConsoleLink } from "@/services/integrationsService";
import { ConnectGoogleAnalyticsModal } from "../../metrics/ConnectGoogleAnalyticsModal";
import { ConnectSearchConsoleModal, searchConsoleSiteLabel } from "../../metrics/ConnectSearchConsoleModal";
import { StepIntro } from "../components";

// Same query keys as ClientConnections, so both views share the cache.
const SOURCES = [
  {
    key: "ga4",
    title: "Google Analytics 4",
    description: "Tráfico, usuarios y conversiones de la web.",
    icon: <GoogleAnalyticsIcon size={22} />,
    fetchLink: getClientAnalyticsLink,
    describe: (link) => link.property_name || link.property_id,
    Modal: ConnectGoogleAnalyticsModal,
  },
  {
    key: "search_console",
    title: "Search Console",
    description: "Clics, impresiones y consultas en Google.",
    icon: <GoogleIcon size={20} />,
    fetchLink: getClientSearchConsoleLink,
    describe: searchConsoleSiteLabel,
    Modal: ConnectSearchConsoleModal,
  },
];

function SourceCard({ source, link, onConnect }) {
  const connected = Boolean(link);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: connected ? "#B7E4C7" : "#E8ECF2",
        backgroundColor: connected ? "#F4FBF6" : "#fff",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: "10px",
          backgroundColor: "#F3F4F6",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {source.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {source.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
          {connected ? source.describe(link) : source.description}
        </Typography>
      </Box>
      {connected ? (
        <Badge color="done" dot={false} sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          <CheckCircle2 size={14} />
          Conectada
        </Badge>
      ) : (
        <Button size="sm" variant="dark" onClick={() => onConnect(source)} sx={{ flexShrink: 0 }}>
          <Link2 size={14} />
          Conectar
        </Button>
      )}
    </Box>
  );
}

SourceCard.propTypes = {
  source: PropTypes.object.isRequired,
  link: PropTypes.object,
  onConnect: PropTypes.func.isRequired,
};

// Optional: GA4 / Search Console are saved right away by their own dialogs, not by the final POST.
export function AnalyticsStep({ clientId, client, hideIntro = false, onBeforeConnect, onConnected }) {
  const queryClient = useQueryClient();
  const { interceptorReady } = useAuth();
  const [connecting, setConnecting] = useState(null); // { source, link }
  const links = useQueries({
    queries: SOURCES.map((source) => ({
      queryKey: ["client-connection", source.key, clientId],
      queryFn: () => source.fetchLink(clientId),
      enabled: interceptorReady && Boolean(clientId),
    })),
  });
  const isLoading = links.some((query) => query.isLoading);

  const Modal = connecting?.source.Modal;
  const setLink = (link) => queryClient.setQueryData(["client-connection", connecting.source.key, clientId], link);

  return (
    <Stack spacing={2.5}>
      {!hideIntro && (
        <StepIntro
          title="Google Analytics y Search Console"
          description="Conecta las fuentes de Google del cliente para completar sus métricas. Es opcional."
        />
      )}

      {isLoading ? (
        <Skeleton variant="rounded" height={74} sx={{ borderRadius: "12px" }} />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 1.5 }}>
          {SOURCES.map((source, index) => (
            <SourceCard
              key={source.key}
              source={source}
              link={links[index].data}
              onConnect={(item) => {
                onBeforeConnect?.();
                setConnecting({ source: item, link: null });
              }}
            />
          ))}
        </Box>
      )}

      <AlertNote variant="info">
        Estas conexiones se guardan al momento. También puedes hacerlo más tarde desde la ficha del cliente.
      </AlertNote>

      {Modal && (
        <Modal
          open
          onOpenChange={(open) => !open && setConnecting(null)}
          clientId={clientId}
          client={client ? { name: client.name, logo: client.logo } : undefined}
          currentLink={connecting.link}
          onConnected={(link) => {
            setLink(link);
            onConnected?.();
          }}
          onDisconnected={() => setLink(null)}
        />
      )}
    </Stack>
  );
}

AnalyticsStep.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  client: PropTypes.object,
  hideIntro: PropTypes.bool,
  onBeforeConnect: PropTypes.func,
  onConnected: PropTypes.func,
};
